import React from 'react';
import queryString from 'query-string';
import $ from 'jquery';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { keys } from 'lodash';

import CalendarHeader from '../calendar-header';
import ActivityList from '../activity-list';
import '../../scss/fs-activity-calendar.scss';

const moment = extendMoment(Moment);

const buildSubcategories = (data) => {
  return data
    .filter(ac => ac.subcategories)
    .map(ac => (
      ac.subcategories.map(sc => ({ ...sc, categoryName: ac.name }))
    ))
    .reduce((acc, curr) => ([...acc, ...curr]), []);
};

const buildActivityStructure = (activities) => {
  const subcategories = buildSubcategories(activities);
  return subcategories.map(sc => (
    sc.activities.map(activity => (
      { ...activity, categoryName: sc.categoryName }
    ))
  )).reduce((acc, curr) => ([...acc, ...curr]), []);
};

const getActivitiesFor = (day, activities) => {
  const daySchedule = {};
  activities.forEach((activity) => {
    activity.schedule[day].forEach((time) => {
      daySchedule[time] = daySchedule[time] || [];
      daySchedule[time].push(activity.activity_id);
    });
  });

  return daySchedule;
};

const buildActivitySchedule = (activities) => {
  return {
    mon: getActivitiesFor('mon', activities),
    tue: getActivitiesFor('tue', activities),
    wed: getActivitiesFor('wed', activities),
    thu: getActivitiesFor('thu', activities),
    fri: getActivitiesFor('fri', activities),
    sat: getActivitiesFor('sat', activities),
    sun: getActivitiesFor('sun', activities),
  };
};

const getWeekRange = (date) => {
  const weekday = date.weekday();
  const startDate = moment(date).subtract(weekday, 'd');
  const endDate = moment(startDate).add(6, 'd');
  return moment.range(startDate.startOf('d'), endDate.endOf('d'));
};

const getActivitiesURL = (property) => {
  const host = global.location.host;
  const protocol = global.location.protocol;
  const contentPath = `/content/en/properties/${property}/activity_categories?&raw=true`;
  let base = 'https://reservations.qa11.stage.fsweb.ca';

  if ((host.indexOf('127.0.0.1') === 0) || (host.indexOf('localhost') === 0)) {
    return base + contentPath;
  }

  if (host.indexOf('m.') === 0) {
    base = host.replace('m.', 'reservations.');
  }

  if (host.indexOf('author.') === 0) {
    if (host.indexOf('author.live.') === 0) {
      base = host.replace('author.live.', 'reservations.');
    } else {
      base = host.replace('author.', 'reservations.');
    }
  }

  if (host.indexOf('www.') === 0) {
    base = host.replace('www.', 'reservations.');
  }

  return `${protocol}//${base}${contentPath}`;
};

class App extends React.Component {
  constructor(props) {
    super(props);

    const locationSearch = queryString.parse(global.location.search);
    const locationDate = moment(locationSearch.selectedDate, 'DD/MM/YYYY');
    const selectedDate = locationDate.isValid() ? locationDate : moment();

    this.state = {
      activities: [],
      isLoading: false,
      schedule: {},
      selectedDate,
      weekRange: getWeekRange(moment(selectedDate)),
    };

    this.updateLocationSearch = this.updateLocationSearch.bind(this);
    this.gotoPreviousWeek = this.gotoPreviousWeek.bind(this);
    this.gotoPreviousDay = this.gotoPreviousDay.bind(this);
    this.gotoNextWeek = this.gotoNextWeek.bind(this);
    this.gotoNextDay = this.gotoNextDay.bind(this);
    this.updateSelectedDate = this.updateSelectedDate.bind(this);
    this.activityIsInRange = this.activityIsInRange.bind(this);
    this.activityIsNotInBlackout = this.activityIsNotInBlackout.bind(this);
  }

  componentDidMount() {
    this.getActivities();
  }

  getActivities() {
    this.setState({ isLoading: true });
    $.ajax({
      url: getActivitiesURL(this.props.property),
      contentType: 'application/json',
      type: 'GET',
      dataType: 'json',
      success: (jsonData) => {
        const json = jsonData.activity_categories;
        const activities = buildActivityStructure(json);
        const schedule = buildActivitySchedule(activities);

        this.setState({
          isLoading: false,
          activities,
          schedule,
        });
      },
    });
  }

  getCurrentActivities() {
    const selectedDay = moment(this.state.selectedDate).format('ddd').toLowerCase();
    const selectedSchedule = this.state.schedule[selectedDay];

    return keys(selectedSchedule)
      .map((time) => {
        const activityIds = selectedSchedule[time];
        return activityIds
          .map(activityId => this.state.activities.find(a => a.activity_id === activityId))
          .filter(Boolean)
          .filter(this.activityIsInRange)
          .filter(this.activityIsNotInBlackout)
          .map(activity => ({
            ...activity,
            time,
            timeFormatted: moment(time, 'HHmm').format('h:mm A'),
          }));
      })
      .reduce((acc, curr) => ([...acc, ...curr]), [])
      .sort((a, b) => parseInt(a.time, 10) - parseInt(b.time, 10));
  }

  activityIsNotInBlackout({ blackoutDates = [] }) {
    const currentDate = moment(this.state.selectedDate);
    const inFormats = ['YYYY-MM-DD', 'YYYY/MM/DD'];
    return blackoutDates.filter(({ startDate, endDate }) => {
      return currentDate.isBetween(
        moment(startDate, inFormats).subtract(1, 'day'),
        moment(endDate, inFormats).add(1, 'day'),
        'day',
      );
    }).length < 1;
  }

  activityIsInRange({ schedule }) {
    const currentDate = moment(this.state.selectedDate);
    const start = moment(schedule.start, 'YYYY-MM-DD').subtract(1, 'day');
    const end = moment(schedule.end, 'YYYY-MM-DD').add(1, 'day');
    return currentDate.isBetween(start, end, 'day');
  }

  gotoPreviousWeek() {
    this.updateSelectedDate(this.state.selectedDate.subtract(7, 'd'));
  }

  gotoPreviousDay() {
    this.updateSelectedDate(this.state.selectedDate.subtract(1, 'd'));
  }

  gotoNextWeek() {
    this.updateSelectedDate(this.state.selectedDate.add(7, 'd'));
  }

  gotoNextDay() {
    this.updateSelectedDate(this.state.selectedDate.add(1, 'd'));
  }

  isPreviousWeekBtnDisabled() {
    const startOfCurrentWeek = moment().day(1);
    return this.state.selectedDate.isSameOrBefore(startOfCurrentWeek);
  }

  isPreviousDayBtnDisabled() {
    const today = moment();
    return this.state.selectedDate.isSameOrBefore(today);
  }

  updateSelectedDate(date) {
    this.setState({
      selectedDate: date,
      weekRange: getWeekRange(date),
    }, this.updateLocationSearch);
  }

  updateLocationSearch() {
    const selectedDate = this.state.selectedDate.format('DD/MM/YYYY');
    global.history.pushState(null, null, `/?selectedDate=${selectedDate}`);
  }

  render() {
    return (
      <div className="fs-activity-calendar">
        <h1 className="title">{ this.props.title }</h1>
        <p className="subtitle">{ this.props.subtitle }</p>

        <main className="main">
          {this.state.isLoading && (
            <p className="fs-activity-calendar-list-no-items">Fetching activities</p>
          )}

          {!this.state.isLoading && <CalendarHeader
            dateRange={this.state.weekRange}
            isPreviousWeekBtnDisabled={this.isPreviousWeekBtnDisabled()}
            isPreviousDayBtnDisabled={this.isPreviousDayBtnDisabled()}
            onPreviousWeekClicked={this.gotoPreviousWeek}
            onPreviousDayClicked={this.gotoPreviousDay}
            onNextWeekClicked={this.gotoNextWeek}
            onNextDayClicked={this.gotoNextDay}
            selectedDate={this.state.selectedDate}
            onDateSelected={this.updateSelectedDate}
          />}

          {!this.state.isLoading &&
            <ActivityList activities={this.getCurrentActivities()} />}
        </main>
      </div>
    );
  }
}

App.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
};


export default App;



// WEBPACK FOOTER //
// ./components/app/index.js